import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import moment from 'moment';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import { HdChartSettingsStore } from '../../chart/bodygraph/chart-tabs/hd-chart-settings.store';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import { HdPlanetInfoService } from '../../chart/bodygraph/hd-planet-info.service';
import {
  HD_PLANET_ORDER_STANDARD,
  HdPlanetKey,
  getHdPlanetDisplayConfig,
} from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import { TransitDataService } from '../../transits/transit-data.service';
import { formatGateLine } from '../../transits/transit-countdown.utils';
import { HdTransitPosition } from '../../transits/transit.models';
import { IChingLinePosition } from '../core/iching.models';
import { injectIChingTranslate } from '../consult/iching-consult-translate';
import { injectIChingHexagramOpener } from './hd-iching-modal-panel';

/** Eén planeetrij in het transitblok. */
export interface IChingTransitRowVm {
  readonly planetKey: HdPlanetKey;
  readonly glyph: string;
  readonly color: string;
  readonly name: string;
  readonly gate: number;
  readonly line: number;
  readonly label: string;
  readonly retrograde: boolean;
  /** De zon: poort = hexagram van de dag, zonnelijn = bewegende lijn (HexWave-analyse, voorstel 2). */
  readonly isSun: boolean;
}

/**
 * De stand van de planeten op het tijdstip van een raadpleging ("welke transit
 * was er toen"): dezelfde weergave als de dagkop van de transits-dag-flow (glyph
 * in de planeetkleur, `poort.lijn`, ℞), geladen via de posities-API. Elke rij
 * opent het klassieke leespaneel op dat hexagram met die lijn in beeld; de zon
 * staat vooraan als "hexagram van de dag". Bewust geen Just Now-paneel: dat
 * vraagt de natal-activaties van een chart, en een raadpleging hoeft geen chart
 * te hebben.
 */
@Component({
  selector: 'hd-iching-transit-block',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host { display: block; }
    .block { border: 1px solid var(--hd-iching-divider, rgba(0, 0, 0, 0.08)); border-radius: var(--radius-md, 6px); padding: 8px 12px; }
    .summary { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 10px; cursor: pointer; font-weight: 600; font-size: 14px; }
    .summary::-webkit-details-marker { display: none; }
    .summary::before { content: '\\25B8'; font-size: 11px; color: var(--primary-75); }
    details[open] .summary::before { content: '\\25BE'; }
    .when { font-weight: 400; font-size: 13px; color: var(--primary-75); }
    /*
     * BELANGRIJK: geen klasse "row" (of andere Bootstrap-namen) op de chips:
     * de globale Bootstrap 4-klasse "row" geeft -15px marge links en rechts, waardoor
     * de planeetchips over elkaar schoven (bugfix 2026-10-18).
     */
    .planets { display: flex; flex-wrap: wrap; gap: 4px 6px; margin-top: 8px; }
    .planet {
      display: inline-flex; align-items: baseline; gap: 4px; max-width: 100%; margin: 0; padding: 2px 8px; border: 1px solid transparent;
      border-radius: var(--radius-md, 6px); background: var(--hd-surface-row-b, #f2efe8); font: inherit; font-size: 13px;
      color: inherit; cursor: pointer; white-space: nowrap;
    }
    .planet:hover { border-color: var(--hd-iching-accent, rgba(212, 168, 105, 0.6)); }
    .planet--sun { background: var(--hd-iching-accent-soft, rgba(212, 168, 105, 0.22)); }
    .glyph { font-family: 'Astronomicon', sans-serif; font-size: 14px; font-weight: 700; line-height: 1; color: var(--planet-color, currentColor); }
    .value { font-weight: 600; }
    .rx { font-size: 11px; color: var(--primary-75); }
    .day-tag { font-size: 11px; color: var(--primary-75); }
    .state { margin: 8px 0 0; font-size: 13px; color: var(--primary-75); }
    .retry { margin-left: 8px; padding: 0; border: 0; background: none; font: inherit; font-size: 12.5px; text-decoration: underline; cursor: pointer; color: inherit; }
  `,
  template: `
    <details class="block" [open]="open()" (toggle)="onToggle($event)" data-test-id="iching-transit-block">
      <summary class="summary">
        <span>{{ t('humanDesign.iching.moment.transitTitle') }}</span>
        <span class="when" data-test-id="iching-transit-when">{{ whenText() }}</span>
      </summary>
      @if (error()) {
        <p class="state" role="alert" data-test-id="iching-transit-error">
          {{ t('humanDesign.iching.moment.transitError') }}
          <button type="button" class="retry" data-test-id="iching-transit-retry" (click)="reload()">{{ t('humanDesign.iching.panel.retry') }}</button>
        </p>
      } @else if (rows() === undefined) {
        <p class="state" role="status" data-test-id="iching-transit-loading">{{ t('humanDesign.iching.moment.transitLoading') }}</p>
      } @else {
        <div class="planets" data-test-id="iching-transit-planets">
          @for (row of rows(); track row.planetKey) {
            <button
              type="button"
              class="planet"
              [class.planet--sun]="row.isSun"
              [style.--planet-color]="row.color"
              [title]="rowTitle(row)"
              (click)="openRow(row)"
              [attr.data-test-id]="'iching-transit-row-' + row.planetKey"
            >
              <span class="glyph" aria-hidden="true">{{ row.glyph }}</span>
              <span class="value">{{ row.label }}</span>
              @if (row.retrograde) {
                <span class="rx" [title]="t('humanDesign.chart.transits.vertical.retrograde')">℞</span>
              }
              @if (row.isSun) {
                <span class="day-tag">{{ t('humanDesign.iching.moment.hexagramOfTheDay') }}</span>
              }
            </button>
          }
        </div>
      }
    </details>
  `,
})
export class HdIChingTransitBlockComponent {
  /** ISO-tijdstip (UTC) waarop de raadpleging gaat. */
  readonly atUtc = input.required<string>();

  private readonly transitData = inject(TransitDataService);
  private readonly settings = inject(HdChartSettingsStore);
  private readonly planetInfo = inject(HdPlanetInfoService);
  /** In de momentdialoog zwevend (anders landt het paneel in de dock achter de dialoog). */
  private readonly openHexagram = injectIChingHexagramOpener();
  private readonly contentLang = inject(HdContentLanguageService);
  protected readonly t = injectIChingTranslate();

  protected readonly open = signal(true);
  protected readonly error = signal(false);
  private readonly positions = signal<readonly HdTransitPosition[] | undefined>(undefined);
  /** Laatste-wint: een ouder antwoord (ander tijdstip) telt niet meer. */
  private generation = 0;

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  /** Minuutnauwkeurig: een seconde-verschil hoeft geen nieuwe lading. */
  private readonly atMinute = computed(() => moment.utc(this.atUtc()).startOf('minute').toISOString());

  private readonly planetKeys = computed<readonly HdPlanetKey[]>(() =>
    this.settings.showChiron() ? [...HD_PLANET_ORDER_STANDARD, 'chiron'] : HD_PLANET_ORDER_STANDARD,
  );

  protected readonly whenText = computed(() =>
    moment.utc(this.atUtc()).local().locale(momentLocaleFor(this.lang())).format('D MMM YYYY, HH:mm'),
  );

  protected readonly rows = computed<readonly IChingTransitRowVm[] | undefined>(() => {
    const positions = this.positions();
    if (positions === undefined) {
      return undefined;
    }
    const lang = this.lang();
    const byKey = new Map(positions.filter(position => position.planetKey != null).map(position => [position.planetKey, position]));
    return this.planetKeys()
      .map((key): IChingTransitRowVm | undefined => {
        const position = byKey.get(key);
        if (position === undefined) {
          return undefined;
        }
        const config = getHdPlanetDisplayConfig(key);
        return {
          planetKey: key,
          glyph: config.astronomiconChar,
          color: config.stripeColor,
          name: this.planetInfo.planetInfoIn(key, lang)?.name || config.label,
          gate: position.gate,
          line: position.line,
          label: formatGateLine(position.gate, position.line),
          retrograde: position.retrograde,
          isSun: key === 'sun',
        };
      })
      .filter((row): row is IChingTransitRowVm => row !== undefined);
  });

  constructor() {
    effect(() => {
      this.atMinute();
      this.planetKeys();
      untracked(() => void this.load());
    });
  }

  protected reload(): void {
    void this.load();
  }

  protected onToggle(event: Event): void {
    this.open.set((event.target as HTMLDetailsElement).open);
  }

  protected rowTitle(row: IChingTransitRowVm): string {
    return row.retrograde ? `${row.name} ${row.label} ℞` : `${row.name} ${row.label}`;
  }

  /** Klik op een planeet: de klassieke tekst van die poort, met de lijn in beeld. */
  protected openRow(row: IChingTransitRowVm): void {
    const focusLine = row.line >= 1 && row.line <= 6 ? (row.line as IChingLinePosition) : undefined;
    this.openHexagram(row.gate, focusLine === undefined ? undefined : { focusLine });
  }

  private async load(): Promise<void> {
    const generation = ++this.generation;
    this.error.set(false);
    this.positions.set(undefined);
    try {
      const positions = await this.transitData.getPositions(moment.utc(this.atMinute()), this.planetKeys());
      if (generation === this.generation) {
        this.positions.set(positions);
      }
    } catch {
      if (generation === this.generation) {
        this.error.set(true);
      }
    }
  }
}
